import React, { FC, Suspense, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { selectUser, setUser } from '../../redux/reducers/userSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedAssignmentSkill } from '../../redux/reducers/assignmentSelectedSkillSlice';
import { selectedExitSlipModal } from '../../redux/reducers/exitSlipModalSlice';
import { AssignmentSidebarContainer } from '../AssignmentSidebar/AssignmentSidebarContainer';
import { Toast } from 'primereact/toast';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';
import { CustomBranding, mapCustomBranding } from '../../models/User';
import { applyCustomBrandingStylesheet } from '../../businessLogicService/customBrandHelper';
import ExitSlipModalWrapper from '../ExitSlip/ExitSlipModalWrapper';
import CustomErrorBoundary from './CustomErrorBoundary';
import * as Sentry from '@sentry/react';
import { usePostHog } from '@posthog/react';
import { isPostHogEnabled } from '../../utils/isPostHogEnabled';
import {
  isGuidedTourPath,
  GUIDED_TOUR_CONTENT_TOP_MARGIN,
  DEFAULT_CONTENT_TOP_MARGIN,
} from '../../pages/guided-tour/guidedTour';

export const LayoutWrapper: FC<React.PropsWithChildren> = ({ children }) => {
  const speechStreamStartPoint = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const hasTopBanner = isGuidedTourPath(pathname);
  const userRedux = useAppSelector(selectUser);
  const assignmentSkillRedux = useAppSelector(selectedAssignmentSkill);
  const exitSlipModalRedux = useAppSelector(selectedExitSlipModal);
  const siteList = userRedux?.sites?.map(e => e.name).join(', ');
  const networkList = userRedux?.sites?.map(e => e.network?.name);
  const uniqueNetworkList = networkList?.filter((item, i, ar) => ar.indexOf(item) === i).join(', ');
  const toast = useRef<Toast>(null);
  const dispatch = useAppDispatch();
  const posthog = usePostHog();

  useEffect(() => {
    window?.userGuiding?.identify(userRedux?.sub ?? '', {
      user_role: userRedux?.roles[0]?.name ?? '',
      grade_level: userRedux?.gradeLevel?.name ?? '',
      user_site: siteList ?? '',
      user_network: uniqueNetworkList ?? '',
      user_hash: userRedux?.userGuidingHash ?? '',
    });
  }, []);

  useEffect(() => {
    if (!userRedux?.sub) return;

    const auth0Id = userRedux?.sub ?? '';
    const role = userRedux?.roles?.[0]?.name ?? '';
    const networkId = userRedux?.network?.id ?? '';
    const gradeLevelId = userRedux?.gradeLevel?.id ?? '';
    const siteIds = userRedux?.sites?.map(s => s.id) ?? [];

    Sentry.setUser(auth0Id ? { id: auth0Id } : null);

    Sentry.setTags({
      user_role: role,
      network_id: networkId,
      grade_level_id: gradeLevelId,
    });

    //Possible max sites per user
    const MAX_SITES_TAGS = 3;

    for (let i = 0; i < MAX_SITES_TAGS; i++) {
      Sentry.setTag(`site_${i}`, siteIds[i] ?? null);
    }

    if (isPostHogEnabled()) {
      posthog?.identify(auth0Id, {
        user_role: role,
        grade_level: userRedux?.gradeLevel?.name ?? '',
        user_site: siteList ?? '',
        user_network: uniqueNetworkList ?? '',
        // siteId (the user's first site) and networkId as scalar person properties so
        // PostHog flag release conditions can target by site or network with `equals` —
        // array properties and the joined `user_site`/`user_network` name strings don't
        // match reliably. The user list is targeted via the built-in Distinct ID.
        siteId: siteIds[0] ?? '',
        networkId,
      });
    }
  }, [userRedux?.sub, userRedux?.network?.id, userRedux?.gradeLevel?.id, userRedux?.sites, userRedux?.roles]);

  useEffect(() => {
    if (!exitSlipModalRedux.visible) return;
    //the lock goes on <html>, not <body>: index.scss sets html{overflow-x:hidden}, which stops
    //body's overflow propagating to the viewport. Locking without resizing keeps the scroll offset.
    document.documentElement.classList.add('tw-overflow-y-hidden');
    return () => document.documentElement.classList.remove('tw-overflow-y-hidden');
  }, [exitSlipModalRedux.visible]);

  useEffect(() => {
    if (!userRedux) return;

    const branding = userRedux.network?.customBranding;
    applyCustomBrandingStylesheet(branding);

    const applied = (branding ? mapCustomBranding[branding] : undefined) ?? CustomBranding.NO_APPLY;
    if (userRedux.customBrandingApplied !== applied) {
      dispatch(setUser({ ...userRedux, customBrandingApplied: applied }));
    }
  }, [userRedux]);

  return (
    <CustomErrorBoundary fallback={<ErrorBoundaryUI />}>
      <div
        className="nc_page tw-w-full min-h-screen flex flex-column"
        style={assignmentSkillRedux.skill ? { overflow: 'clip', height: '100vh' } : {}}
      >
        <Toast ref={toast} position="bottom-center" />
        {assignmentSkillRedux.skill && (
          <Suspense>
            <AssignmentSidebarContainer selectedSkillForAssignment={assignmentSkillRedux.skill} toast={toast.current} />
          </Suspense>
        )}
        {exitSlipModalRedux.visible && <ExitSlipModalWrapper />}
        <Header />
        <main role="main">
          <div
            className={`flex-grow-1 flex flex-column ${
              hasTopBanner ? GUIDED_TOUR_CONTENT_TOP_MARGIN : DEFAULT_CONTENT_TOP_MARGIN
            }`}
            ref={speechStreamStartPoint}
          >
            {children}
          </div>
        </main>
        <Footer speechStreamStartPoint={speechStreamStartPoint} />
      </div>
    </CustomErrorBoundary>
  );
};
